import { CodexCategorySchema, CodexEntryField, CodexEntrySchema, CodexEntryType, CodexFieldType, CoreEntityType } from '#/index';
import { CodexDetail, ListContainer, ExecuteParams, ExecutionContext, PromptFunction, TextContainer } from '../../types';
import { getCodexEntryFields, getCodexEntryFromPosition } from '../../utils';
import { CodexUtils } from '../../utils/codex';
import { createCustomFunction } from './createFunction';


const getCodexEntryByElements = (context: ExecutionContext, elements: ExecuteParams[]): CodexEntrySchema[] => {
  const codex = new CodexUtils(context);
  elements.forEach(element => {
    if (typeof element === 'object' && element.type === CoreEntityType.NovelActs) {
      codex.byAct(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelChapters) {
      codex.byChapter(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelScenes) {
      codex.byScene(element)
    } else if (typeof element === 'object' && element.type === 'outlineTree') {
      codex.byOutline(element)
    } else if (typeof element === 'object' && element.type === 'novelLabel') {
      codex.byLabel(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexCategories) {
      codex.byCodexCategory(element)
    } else if (typeof element === 'object' && element.type === 'codexDetail') {
      codex.byCodexDetail(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.Novels) {
      codex.byNovel(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelSeries) {
      codex.bySeries(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.Snippets) {
      codex.bySnippet(element)
    } else if (typeof element === 'object' && element.type === 'pointOfView') {
      codex.byPov(element)
    } else if (typeof element === 'string') {
      codex.byText(element)
    } else if (typeof element === 'object' && element.type === 'textContainer' && element.reference) {
      getCodexEntryByElements(context, [element.reference]).forEach(entry => {
        codex.byCodexEntry(entry)
      })
    } else if (typeof element === 'object' && element.type === 'listContainer' && element.elements) {
      getCodexEntryByElements(context, element.elements).forEach(entry => {
        codex.byCodexEntry(entry)
      })
    }
  })

  return codex.getResult()
}


/**
 * 设定条目相关函数
 * @returns 设定条目相关函数对象
 */
const codexFunctions: Record<string, PromptFunction> = {
  'codex.all': createCustomFunction(
    'codex.all',
    '返回所有Codex条目，或从给定参数中提取它们。',
    (context, ...params: ExecuteParams[]): ListContainer<CodexEntrySchema> => {
      let result: CodexEntrySchema[] = []
      if (params.length) {
        result = getCodexEntryByElements(context, params)
      } else {
        result = context.globalContext.generateStore.getState().codexEntries
      }
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    },
    // formats
  ),

  'codex.aliases': createCustomFunction(
    'codex.aliases',
    '返回给定参数中每个可能的"Codex"条目的别名，未提供参数时返回当前上下文的Codex条目别名',
    // 上下文指用户提供的输入以及当前场景中的Codex条目（包含正文和摘要内提及的codex）
    (context, ...params: ExecuteParams[]): ListContainer<TextContainer> => {
      let result: CodexEntrySchema[] = []
      if (params.length) {
        result = getCodexEntryByElements(context, params)
      } else {
        result = getCodexEntryFromPosition(context)
      }

      return {
        type: 'listContainer',
        defaultFormat: 'inherited',
        elements: result.map(entry => {
          return {
            type: 'textContainer' as const,
            reference: entry,
            title: entry.attributes.aliases,
            attributes: {
              name: entry.attributes.name,
              aliases: entry.attributes.aliases,
            },
            defaultFormat: 'plain',
            formatters: {
              plain: (container) => container.title
            }
          }
        })
      }
    }
  ),
  'codex.category': createCustomFunction(
    'codex.category',
    '按名称返回自定义Codex类别，或返回给定参数中每个可能的"Codex"条目',
    (context, ...params: ExecuteParams[]): ListContainer<CodexCategorySchema> => {
      const { codexEntryGroups } = context.globalContext.generateStore.getState();
      const entriesIds: string[] = getCodexEntryByElements(context, params).map(entry => entry.id)
      const result = codexEntryGroups.map(group => {
        if (group.children.some(entry => entriesIds.includes(entry.id))) {
          return group.category
        }
      }).filter(group => group) as CodexCategorySchema[]

      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  "codex.detail": createCustomFunction(
    'codex.detail',
    '返回给定参数中每个可能的"Codex"条目的自定义详情，未提供参数时返回当前上下文的Codex条目自定义详情',
    (context, ...params: ExecuteParams[]): ListContainer<CodexDetail> => {
      const { codexFields } = context.globalContext.generateStore.getState()
      const result: CodexDetail[] = [];
      // 选中的条目
      let entries: CodexEntrySchema[] = [];

      // 目标字段ID和条目
      let targetFieldIds: string[] = [];
      // 目标条目
      let targetEntries: CodexEntrySchema[] = [];
      const isValid = (field: CodexEntryField) => {
        const detail = codexFields.find(item => item.id === field.fieldId)
        if (detail?.attributes.type === CodexFieldType.Text) {
          return Boolean(field.content.textarea)
        } else if (detail?.attributes.type === CodexFieldType.Line) {
          return Boolean(field.content.lineText)
        } else if (detail?.attributes.type === CodexFieldType.Dropdown) {
          return Boolean(field.content.select?.length)
        } else if (detail?.attributes.type === CodexFieldType.Codex) {
          return Boolean(field.content.codex?.length)
        }
        return false
      }
      if (params.slice(0, -1).length) {
        entries = getCodexEntryByElements(context, params.slice(0, -1))
      } else {
        entries = getCodexEntryFromPosition(context)
      }

      if (params.length) {
        const param = params[params.length - 1]
        if (typeof param === 'string') {
          const fieldId = codexFields.find(field => field.attributes.name === param)?.id
          if (fieldId) {
            targetFieldIds.push(fieldId)
          }
        } else if (typeof param === 'object') {
          targetEntries = getCodexEntryByElements(context, [param]);
          targetEntries.forEach(entry => {
            entry.fields?.forEach(field => {
              if (isValid(field) && !targetFieldIds.includes(field.fieldId)) targetFieldIds.push(field.fieldId)
            })
          })
        }
      }

      entries.forEach(entry => {
        const filedValues = getCodexEntryFields(context, entry)
        const codexDetail: CodexDetail = {
          type: 'codexDetail',
          entry: entry,
          fields: [],

        }
        entry.fields?.forEach(field => {
          const fieldDetail = codexFields.find(d => d.id === field.fieldId)
          const detail = filedValues.find(d => d.fieldId === field.fieldId)
          if (targetFieldIds.includes(field.fieldId) && detail && fieldDetail) {
            codexDetail.fields.push({
              name: detail.name || '',
              value: detail.value || '',
              reference: fieldDetail,
            })
          }
        })
      })

      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    },
    [{ name: 'elements', type: 'any', required: false }, { name: 'detailName', type: 'string', required: false }]
  ),
  'codex.characters': createCustomFunction(
    'codex.characters',
    '返回/提取所有"Character"类型的Codex条目。',
    (context, ...params: ExecuteParams[]): ListContainer<CodexEntrySchema> => {
      let result: CodexEntrySchema[] = []
      if (!params.length) {
        result = context.globalContext.generateStore.getState().codexEntries.filter(entry => entry.attributes.type === CodexEntryType.Character)
      } else {
        result = getCodexEntryByElements(context, params).filter(entry => entry.attributes.type === CodexEntryType.Character)
      }
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    },
  ),
  'codex.context': createCustomFunction(
    'codex.context',
    '返回当前上下文的Codex条目，或过滤给定参数是否在当前上下文的Codex条目中',
    (context, ...params: ExecuteParams[]): ListContainer<CodexEntrySchema> | void => {
      let result: CodexEntrySchema[] = getCodexEntryFromPosition(context)

      if (params.length) {
        const ids = getCodexEntryByElements(context, params).map(d => d.id)
        result = result.filter(d => ids.includes(d.id))
      }

      if (!result.length) return;
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    },
  ),
  'codex.description': createCustomFunction(
    'codex.description',
    '返回给定参数中每个可能的"Codex"条目的描述，未提供参数时返回当前上下文的Codex条目描述',
    (context, ...params: ExecuteParams[]): ListContainer<CodexEntrySchema> | void => {
      let result: CodexEntrySchema[] = []
      if (params.length) {
        result = getCodexEntryByElements(context, params)
      } else {
        result = getCodexEntryFromPosition(context)
      }
      if (!result.length) return;
      return {
        type: 'listContainer',
        elements: result.filter(d => d.attributes.description),
        defaultFormat: 'inherited',
      }
    },
  ),
  'codex.get': createCustomFunction(
    'codex.get',
    '通过名称或别名获取一个或多个Codex条目，或从给定参数中提取它们',
    (context, ...params: ExecuteParams[]): ListContainer<CodexEntrySchema> => {
      let result: CodexEntrySchema[] = []
      if (params.length) {
        result = getCodexEntryByElements(context, params)
      }
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    },
  ),
  'codex.global': createCustomFunction(
    'codex.global',
    '返回所有标记为全局（始终包含）的Codex条目，或从给定参数中提取它们',
    (context, ...params: ExecuteParams[]): ListContainer<CodexEntrySchema> => {
      let result: CodexEntrySchema[] = []
      if (params.length) {
        result = getCodexEntryByElements(context, params)
      } else {
        result = context.globalContext.generateStore.getState().codexEntries;
      }
      result = result.filter(entry => entry.attributes.alwaysInclude)
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    },
  ),
  'codex.has': createCustomFunction(
    'codex.has',
    '检查给定参数中是否包含任何（有效的）Codex条目',
    (context, ...params: ExecuteParams[]): boolean => {
      let result: CodexEntrySchema[] = []
      if (params.length) {
        result = getCodexEntryByElements(context, params)
      }
      result = result.filter(entry => entry.attributes.name && !entry.attributes.hideFromAi)
      return Boolean(result.length)
    },
  ),
}

export default codexFunctions;